<script setup>
import { WdsIcon } from '@wds/components';
import trustCuesReviewsMocks from './index.mock.json';

// ## COMPOSABLES
const {
  locale,
  locales,
  defaultLocaleObj,
  t,
} = useI18n();
const device = useDevice();
const { $tracking } = useNuxtApp();

// ## CONSTANTS
const MOBILE_APP_DOWNLOAD_LINK = 'https://hostelworld.onelink.me/IdiK/vz1m10ma';
const DESKTOP_APP_DOWNLOAD_LINKS = {
  apple: 'https://hostelworld.onelink.me/IdiK/mhcpyui2',
  android: 'https://hostelworld.onelink.me/IdiK/436by7e0',
};

// Pre-create arrays for star icons to avoid recreation on every render
const APP_STORE_STARS = [0, 1, 2, 3, 4];
const GOOGLE_PLAY_STARS = [0, 1, 2, 3];

// App store ratings data
const APP_STORE_RATING = {
  score: '4.9',
  installs: t('t_HOMEPAGETRUSTCUESAPPSTOREINSTALLS'),
  stars: APP_STORE_STARS,
};

const GOOGLE_PLAY_RATING = {
  score: '4.74',
  installs: t('t_HOMEPAGETRUSTCUESGOOGLEPLAYINSTALLS'),
  stars: GOOGLE_PLAY_STARS,
};

// ## COMPUTED
const currentLocale = computed(() => locales.value.find((l) => l.code === locale.value) || defaultLocaleObj);

const displayAppleStoreDownloadBadge = computed(() => {
  return (device?.isMobileOrTablet && device?.isIos) || device?.isDesktop;
});

const displayGooglePlayDownloadBadge = computed(() => {
  return (device?.isMobileOrTablet && !device?.isIos) || device?.isDesktop;
});

const getAppleStoreDownloadLink = computed(() => {
  return device?.isMobileOrTablet ? MOBILE_APP_DOWNLOAD_LINK : DESKTOP_APP_DOWNLOAD_LINKS.apple;
});

const getGooglePlayDownloadLink = computed(() => {
  return device?.isMobileOrTablet ? MOBILE_APP_DOWNLOAD_LINK : DESKTOP_APP_DOWNLOAD_LINKS.android;
});

// Memoize app store image URLs to avoid recalculation
const appStoreImageUrl = computed(
  () => `https://sc.hwstatic.com/wds/logos/app-badges/app-store/${currentLocale.value.isoUnder}.png`,
);

const googlePlayImageUrl = computed(
  () => `https://sc.hwstatic.com/wds/logos/app-badges/google-play/${currentLocale.value.isoUnder}.png`,
);

// ## REFS
const trustCuesRef = ref(null);
const intersectionObserver = ref(null);

// ## METHODS
function handleAppDownloadClick(os) {
  const segmentVariables = {
    action: 'Travel Cues App Store Clicked',
    os_button_clicked: os,
  };

  $tracking.onTrackEvent(segmentVariables);
}

function handleViewportTracking() {
  const segmentVariables = { action: 'Trust Cues Section Viewed' };

  $tracking.onTrackEvent(segmentVariables);
}

// ## LIFECYCLE
onMounted(() => {
  if (!trustCuesRef.value) {
    return;
  }

  try {
    intersectionObserver.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleViewportTracking();
            intersectionObserver.value?.unobserve(entry.target);
            intersectionObserver.value?.disconnect();
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px',
      },
    );

    intersectionObserver.value.observe(trustCuesRef.value);
  } catch (error) {
    console.error('### Failed to initialize intersection observer:', error);
  }
});

onUnmounted(() => {
  if (intersectionObserver.value) {
    intersectionObserver.value.disconnect();
    intersectionObserver.value = null;
  }
});
</script>

<template>
  <section
    ref="trustCuesRef"
    class="trust-cues-container"
    role="region"
    :aria-label="$t('t_HOMEPAGETRUSTCUESTITLE')"
  >
    <div class="trust-cues-image">
      <div class="trust-cues-download-apps">
        <div
          v-if="displayAppleStoreDownloadBadge"
          class="trust-cues-download-app app-store"
        >
          <div class="app-score">
            <WdsIcon
              v-for="i in APP_STORE_RATING.stars"
              :key="`app-store-star-${i}`"
              asset="star"
              size="16"
              height="10"
              width="10"
              :aria-hidden="true"
            />
            <span>{{ APP_STORE_RATING.score }}</span>
          </div>
          <span class="app-num-installs">{{ APP_STORE_RATING.installs }}</span>
          <a
            :href="getAppleStoreDownloadLink"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="$t('t_TEXTDOWNLOADONAPPSTORE')"
            @click="handleAppDownloadClick('iOS')"
          >
            <img
              :alt="$t('t_TEXTDOWNLOADONAPPSTORE')"
              :title="$t('t_TEXTDOWNLOADONAPPSTORE')"
              :src="appStoreImageUrl"
              loading="lazy"
              height="48"
              width="120"
            />
          </a>
        </div>
        <div
          v-if="displayGooglePlayDownloadBadge"
          class="trust-cues-download-app google-play"
        >
          <div class="app-score">
            <WdsIcon
              v-for="i in GOOGLE_PLAY_RATING.stars"
              :key="`google-play-star-${i}`"
              asset="star"
              size="16"
              height="10"
              width="10"
              :aria-hidden="true"
            />
            <WdsIcon
              asset="star-half"
              size="16"
              height="10"
              width="10"
              :aria-hidden="true"
            />
            <span>{{ GOOGLE_PLAY_RATING.score }}</span>
          </div>
          <span class="app-num-installs">{{ GOOGLE_PLAY_RATING.installs }}</span>
          <a
            :href="getGooglePlayDownloadLink"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="$t('t_TEXTDOWNLOADONPLAYSTORE')"
            @click="handleAppDownloadClick('Android')"
          >
            <img
              :alt="$t('t_TEXTDOWNLOADONPLAYSTORE')"
              :title="$t('t_TEXTDOWNLOADONPLAYSTORE')"
              :src="googlePlayImageUrl"
              loading="lazy"
              height="48"
              width="120"
            />
          </a>
        </div>
      </div>
    </div>
    <h2
      class="trust-cues-title"
      v-html="$t('t_HOMEPAGETRUSTCUESTITLE')"
    />
    <div class="trust-cues-subtitle">
      {{ $t('t_HOMEPAGETRUSTCUESSUBTITLE') }}
    </div>
    <div class="trust-cues-review-panel">
      <div class="trust-cues-reviews-wrapper">
        <div
          class="trust-cues-reviews-track"
          role="list"
          :aria-label="$t('t_HOMEPAGETRUSTCUESSUBTITLE')"
        >
          <HomepageHwTrustCuesReviewsCard
            v-for="(userReview, i) in trustCuesReviewsMocks"
            :key="`user-review-card-${i}`"
            :user="userReview.user"
            :review-text="$t(`t_HOMEPAGETRUSTCUESREVIEW${i + 1}`)"
            class="trust-cues-review"
            role="listitem"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
$section-spacing-mobile: $wds-spacing-m;
$section-spacing-tablet: $wds-spacing-l;
$section-spacing-desktop: wds-rem(40px);

.trust-cues-container {
  display: flex;
  flex-direction: column;

  .trust-cues-image {
    grid-area: image;
    position: relative;
    width: auto;
    height: wds-rem(200px);
    border-radius: $wds-border-radius-m;
    background-image: url('@/assets/images/homepage/trust-cues-reviews/trust-block-image-sm.jpg');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    margin-bottom: $wds-spacing-xl;
    margin-inline: $section-spacing-mobile;

    .trust-cues-download-apps {
      position: absolute;
      bottom: -$wds-spacing-l;
      right: $wds-spacing-m;
      display: inline-flex;
      gap: $wds-spacing-m;

      .trust-cues-download-app {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        padding: $wds-spacing-s;
        width: wds-rem(136px);
        background-color: $wds-color-white;
        border-radius: $wds-border-radius-l;
        box-shadow:
          inset 0 0 0 wds-rem(1px) $wds-color-ink-lightest,
          $wds-shadow-light-s;
        transition: transform 0.2s ease-in-out;

        &:hover {
          transform: translateY(-2px);
        }

        &:focus-within {
          outline: 2px solid $wds-color-purple;
          outline-offset: 2px;
        }

        .app-score {
          display: inline-flex;
          gap: $wds-spacing-xxs;
          place-items: center;

          @include body-2-bld;

          > span {
            margin-left: $wds-spacing-xxs;
          }
        }

        .app-num-installs {
          margin-bottom: $wds-spacing-xs;

          @include body-3-reg;
        }
      }
    }
  }

  .trust-cues-title {
    grid-area: title;
    margin-inline: $wds-spacing-m;
    overflow-wrap: break-word;

    @include title-1-bld;

    :deep(span) {
      color: $wds-color-purple;
    }
  }

  .trust-cues-subtitle {
    grid-area: subtitle;
    margin-inline: $wds-spacing-m;
    margin-bottom: $wds-spacing-l;

    @include body-1-reg;
  }

  .trust-cues-review-panel {
    display: block;

    .trust-cues-reviews-wrapper {
      display: block;

      .trust-cues-reviews-track {
        display: flex;
        overflow-x: auto;
        scroll-behavior: smooth;
        scroll-snap-type: x mandatory;
        scroll-padding-left: $section-spacing-mobile;
        will-change: scroll-position;

        @include no-scrollbar;

        .trust-cues-review {
          min-width: wds-rem(248px);
          width: 100%;
          margin: $wds-spacing-m $wds-spacing-s;
          scroll-snap-align: start;

          &:first-child {
            margin-left: $section-spacing-mobile;
          }

          &:last-child {
            margin-right: $section-spacing-mobile;
          }
        }
      }
    }
  }

  @include tablet {
    .trust-cues-title {
      @include lg-title-3-bld;
    }
  }

  @include tablet-large {
    .trust-cues-image,
    .trust-cues-title,
    .trust-cues-subtitle {
      margin-inline: $section-spacing-tablet;
    }

    .trust-cues-review-panel .trust-cues-reviews-wrapper .trust-cues-reviews-track {
      scroll-padding-left: $section-spacing-tablet;

      .trust-cues-review {
        &:first-child {
          margin-left: $section-spacing-tablet;
        }

        &:last-child {
          margin-right: $section-spacing-tablet;
        }
      }
    }
  }

  @include desktop {
    display: grid;
    grid-template:
      'image title' auto
      'image subtitle' auto
      'image cards' auto / 357px 1fr;
    gap: $wds-spacing-s $wds-spacing-xxl;

    .trust-cues-title,
    .trust-cues-subtitle {
      margin-inline: $wds-spacing-m 0;
    }

    .trust-cues-image {
      background-image: url('@/assets/images/homepage/trust-cues-reviews/trust-block-image-lg.jpg');
      height: auto;
      margin: 0;

      .trust-cues-download-apps {
        bottom: $wds-spacing-m;
        right: -$wds-spacing-l;
      }
    }

    .trust-cues-review-panel {
      position: relative;
      display: block;

      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: 0;
        right: 0;
        background: linear-gradient(180deg, $wds-color-white 0%, rgb(255 255 255 / 0%) 100%);
        height: 48px;
        z-index: 1;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 48px;
        background: linear-gradient(0deg, $wds-color-white 0%, rgb(255 255 255 / 0%) 100%);
        z-index: 1;
      }

      .trust-cues-reviews-wrapper {
        height: wds-rem(420px);
        overflow-y: auto;

        .trust-cues-reviews-track {
          display: grid;
          grid-template-columns: repeat(2, auto);
          padding: $wds-spacing-m;
          gap: $wds-spacing-m;

          .trust-cues-review {
            max-width: 100%;
            min-width: wds-rem(200px);
            margin: 0;

            &:first-child {
              margin-left: 0;
            }

            &:last-child {
              margin-right: 0;
            }
          }
        }
      }
    }
  }

  @include desktop-large {
    .trust-cues-review-panel .trust-cues-reviews-wrapper .trust-cues-reviews-track {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}
</style>
